/**
 * Audience membership and prospect status are two independent axes (ORB-619), not one three-way.
 *
 * Audience membership is "was this person targeted by this campaign". Prospect status is "is this
 * person a real prospect at all". They cross:
 *
 *                | prospect                | non-prospect
 *   audience     | targeted prospect       | a colleague who was on the file
 *   non-audience | genuine inbound lead    | our own staff, the customer's, a guest co-host
 *
 * Collapsing them into one control would make the most valuable view unreachable — non-audience
 * prospects, i.e. "who came to us that we weren't already targeting". So the audience control stays
 * a three-state selector and non-prospects are hidden by default across all three, with a separate
 * switch to reveal them.
 *
 * Lives here rather than in each screen because the same filter previously sat copy-pasted in three
 * components, which is how the "Non-Audience" state stayed wrong in all of them.
 */
export type AudienceView = 'audience' | 'nonAudience' | 'all'

export const DEFAULT_AUDIENCE_VIEW: AudienceView = 'audience'

/**
 * A missing IsAudienceTarget counts as audience. The server always sends the flag (it is a computed
 * non-nullable bool), but the TS type marks it optional, and treating undefined as non-audience
 * would make a lead vanish from both the Audience and Non-Audience views and appear only under All.
 */
const isAudienceMember = (lead: AudienceFilterable): boolean => lead.IsAudienceTarget !== false

/**
 * What this filter needs off a row. Structural rather than a named DTO because the same rule now
 * runs over lead timelines and over file-import rows, which share no base type. A file-import row
 * carries no IsAudienceTarget at all — every row of an import IS the audience — and the rule above
 * reads that absence as audience, which is the right answer there.
 */
export type AudienceFilterable = { IsNonProspect?: boolean; IsAudienceTarget?: boolean }

/**
 * Turning non-prospects on is a MODE, not an addition: it shows those people and only those people,
 * and the audience selector is disabled while it is on. Asked for on the Ortec rollout call — the
 * point of looking at them is to check who is being excluded, and mixing them back in with the
 * prospects defeats that.
 */
export function filterLeadsByAudience<T extends AudienceFilterable>(
  leads: readonly T[],
  view: AudienceView,
  showNonProspects: boolean,
): T[] {
  if (showNonProspects) {
    return leads.filter((lead) => lead.IsNonProspect)
  }

  const visible = leads.filter((lead) => !lead.IsNonProspect)

  switch (view) {
    case 'audience':
      return visible.filter(isAudienceMember)
    case 'nonAudience':
      // The true complement. This used to return everything, which is the bug ORB-619 names.
      return visible.filter((lead) => !isAudienceMember(lead))
    case 'all':
      return [...visible]
  }
}

/**
 * Companies get the same three-state control as Contacts. A company counts as audience when ANY of
 * its people were targeted (the server decides), and as a non-prospect only when EVERY lead behind
 * it is one — so a company with a single real prospect stays, with a reduced count.
 */
export function filterCompaniesByAudience<T extends AudienceFilterable>(
  companies: readonly T[],
  view: AudienceView,
  showNonProspects: boolean,
): T[] {
  if (showNonProspects) {
    return companies.filter((company) => company.IsNonProspect)
  }

  const visible = companies.filter((company) => !company.IsNonProspect)

  switch (view) {
    case 'audience':
      return visible.filter((company) => company.IsAudienceTarget !== false)
    case 'nonAudience':
      return visible.filter((company) => company.IsAudienceTarget === false)
    case 'all':
      return [...visible]
  }
}

/**
 * Empty-state copy for the current selection, so each screen does not invent its own wording.
 *
 * `hiddenCount` is how many leads the current filter is holding back. When it is above zero the
 * table is empty but the data is not, and saying "no leads yet" beside a conversions counter reading
 * 1 makes a working ingest look broken — which is exactly how it was read in testing. Name the
 * filter doing the hiding instead, and let the screen offer to clear it.
 */
export function emptyStateMessage(
  view: AudienceView,
  showNonProspects: boolean,
  hiddenCount = 0,
): string {
  if (hiddenCount > 0) {
    const noun = hiddenCount === 1 ? 'lead is' : 'leads are'
    return showNonProspects
      ? `${hiddenCount} ${noun} here, hidden by the current filter.`
      : `${hiddenCount} ${noun} hidden by the current filter.`
  }

  if (showNonProspects) {
    return 'No non-prospects here.'
  }

  const suffix = ' Non-prospects are hidden.'
  switch (view) {
    case 'audience':
      return `No audience leads yet.${suffix}`
    case 'nonAudience':
      return `No non-audience leads yet.${suffix}`
    case 'all':
      return `No leads yet.${suffix}`
  }
}
